import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useThemeMode } from "../Context/ThemeContext";
import AppFooter from "./AppFooter";
import { getHlinkInstallDisplay, getSensorInstallDisplay, isMonitoringSensorId } from "../utils/installationInfo";
import { isSensorOverheat } from "../utils/wifiTempAlarm";
import {
  UNASSIGNED,
  buildSiteIndex,
  compareCountries,
  compareNames,
  countryName,
  hlinkLocationName,
  hlinkSiteName,
} from "../utils/siteNames";
import { resolveDeviceWorldPosition } from "../utils/worldMapLocations";
import DashboardSiteMap from "./DashboardSiteMap";
import { sendTripReset } from "../utils/deviceCommands";
import { isDeviceOnline } from "../utils/deviceStatus";
import "./DashboardView.css";

/** SVG strokes need concrete colours; same hues as the CSS palette. */
const CHART = {
  dark: { ir: "#5eead4", env: "#60a5fa", hum: "#7c8cff", grid: "#24344f", muted: "#93a4c3", tooltip: "#121b30", line: "#2c3d5e", text: "#f5f7fb" },
  light: { ir: "#0891b2", env: "#2563eb", hum: "#6d5efc", grid: "#e2e8f0", muted: "#64748b", tooltip: "#ffffff", line: "#e2e8f0", text: "#1e293b" },
};

const STATUS_LABEL = { normal: "Normal", warning: "Warning", emergency: "Emergency", offline: "No Response" };
const METER_BARS = 5;
const SPOT_CELLS = 9;
const classify = (sensor) => {
  if (Number(sensor?.com_state) !== 1) return "offline";
  const level = Number(sensor?.arc_level) || 0;
  if (level >= 5 || sensor?.arc_state) return "emergency";
  if (level >= 3) return "warning";
  return "normal";
};

const worstStatus = (statuses) =>
  statuses.includes("emergency") ? "emergency" : statuses.includes("warning") ? "warning" : null;

/** An offline H-LINK's last sensor values are stale, so it has no live sensors. */
const liveSensors = (device) =>
  (isDeviceOnline(device) ? device.sensors || [] : [])
    .filter((s) => isMonitoringSensorId(s?.sensor_id) && Number(s?.com_state) === 1)
    .sort((a, b) => Number(a.sensor_id) - Number(b.sensor_id));

const serialOf = (device) => String(device?.device_info?.serial_number || "");

/** Trip units reported in the device's `trip` list. Trip_State 1 = MCB tripped, 0 = MCB up. */
const readTrips = (device) => {
  const list = Array.isArray(device?.trip) ? device.trip : Array.isArray(device?.device_info?.trip) ? device.device_info.trip : [];
  return list
    .filter((t) => t && typeof t === "object")
    .map((t, i) => ({
      key: String(t.id ?? t.sensor_id ?? i),
      sensorId: t.sensor_id ?? null,
      serial: String(t.TRIP_SerialNumber ?? t.trip_serial_number ?? "").trim(),
      state: Number(t.Trip_State ?? t.trip_state) === 1 ? 1 : 0,
    }));
};

const PROTOCOL_LABEL = { tcpip: "TCP/IP", wifi: "WiFi", modebus: "TCP/IP MODBUS", modbus: "TCP/IP MODBUS", lte: "LTE" };

/** The H-LINK's own link type; the stream-wide value is "Offline" whenever it is missing, so it is only a fallback. */
const protocolOf = (device, streamProtocol) => {
  const raw = [
    device?.connection_state,
    device?.connection_type,
    device?.protocol,
    device?.device_info?.connection_state,
    device?.device_info?.connection_type,
    device?.device_info?.protocol,
  ].find((v) => typeof v === "string" && v.trim());
  if (raw) return PROTOCOL_LABEL[raw.trim().toLowerCase()] || raw.trim().toUpperCase();
  return streamProtocol && streamProtocol !== "Offline" ? streamProtocol : "—";
};

const flagEmoji = (code) => {
  const cc = String(code || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return "🏳";
  return String.fromCodePoint(...[...cc].map((c) => 127397 + c.charCodeAt(0)));
};

const formatStamp = (date) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())} ${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`;
};

const formatClock = (date) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${p(date.getDate())}/${p(date.getMonth() + 1)}/${date.getFullYear()} ${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`;
};

/** Ticks on its own so the per-second update does not re-render the dashboard. */
function LiveClock() {
  const [now, setNow] = useState(() => formatClock(new Date()));
  useEffect(() => {
    const id = setInterval(() => setNow(formatClock(new Date())), 1000);
    return () => clearInterval(id);
  }, []);
  return <span className="pill">{now}</span>;
}

const fmt1 = (v) => (v == null || !Number.isFinite(v) ? "—" : v.toFixed(1));

const seriesStats = (values) => {
  const v = values.filter(Number.isFinite);
  if (!v.length) return { min: null, avg: null, max: null };
  return { min: Math.min(...v), avg: v.reduce((a, b) => a + b, 0) / v.length, max: Math.max(...v) };
};

const coord = (v) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));

const FIELD_SEP = "\u0001";
const ROW_SEP = "\u0002";

/** Live serials plus the device fields placement reads; an unchanged key means unchanged placements. */
const locationKeyOf = (devices) =>
  (devices || [])
    .map((d) => {
      const info = d?.device_info || {};
      return [serialOf(d), info.country || "", info.region || "", info.city || ""].join(FIELD_SEP);
    })
    .join(ROW_SEP);

/**
 * Site, company, label and map position per H-LINK known from backend sites, saved Settings and
 * the live stream. Depends on installation info, not on live readings.
 */
const buildPlacements = (locationKey, installStore) => {
  const index = buildSiteIndex(installStore);
  const stored = installStore?.hlinks || {};
  const infoBySerial = new Map(
    (locationKey ? locationKey.split(ROW_SEP) : []).map((row) => {
      const [serial, country, region, city] = row.split(FIELD_SEP);
      return [serial, { country, region, city }];
    })
  );
  const serials = [...new Set([...infoBySerial.keys(), ...index.keys(), ...Object.keys(stored)])].filter(Boolean);
  return serials.map((serial) => {
    const info = infoBySerial.get(serial) || {};
    const entry = index.get(serial);
    const display = getHlinkInstallDisplay(installStore, serial);
    const code = String(entry?.site?.country || info.country || "").trim();
    const site = entry?.site || null;
    const pos = resolveDeviceWorldPosition({
      latitude: coord(entry?.device?.latitude) ?? coord(site?.latitude),
      longitude: coord(entry?.device?.longitude) ?? coord(site?.longitude),
      address: site?.address || "",
      siteName: site?.name || "",
      regionName: site?.region || info.region || "",
      city: info.city || "",
      country: countryName(code) || info.country || "",
      countryCode: code.toUpperCase(),
      install: display.matched ? { installation_location: display.location } : {},
    });
    return {
      serial,
      company: entry?.company?.name || "",
      country: countryName(code) || "Unassigned",
      countryCode: /^[A-Za-z]{2}$/.test(code) ? code.toUpperCase() : "",
      site: hlinkSiteName(serial, stored[serial], info, entry),
      label: hlinkLocationName(entry) || (display.matched ? display.location : "") || `H-LINK ${serial}`,
      pos,
    };
  });
};

/** One row per H-LINK: its placement plus live state. */
const buildHlinks = (placements, devices) => {
  const liveBySerial = new Map((devices || []).map((d) => [serialOf(d), d]));
  return placements.map((placement) => {
    const device = liveBySerial.get(placement.serial);
    const sensors = liveSensors(device);
    const online = isDeviceOnline(device);
    return {
      ...placement,
      live: Boolean(device),
      online,
      sensors: sensors.length,
      status: online ? worstStatus(sensors.map(classify)) || "normal" : "offline",
    };
  });
};

const byLabel = (a, b) => compareNames(a.label, b.label);

/** Country → company → site → H-LINK. */
const buildTree = (hlinks) => {
  const countries = new Map();
  hlinks.forEach((h) => {
    if (!countries.has(h.country)) {
      countries.set(h.country, {
        key: `c:${h.country}`,
        label: h.country,
        icon: flagEmoji(h.countryCode),
        bold: true,
        code: h.countryCode,
        companies: new Map(),
      });
    }
    const country = countries.get(h.country);
    const companyName = h.company || UNASSIGNED;
    if (!country.companies.has(companyName)) {
      country.companies.set(companyName, {
        key: `${country.key}|co:${companyName}`,
        label: companyName,
        icon: "🏭",
        bold: true,
        sites: new Map(),
      });
    }
    const company = country.companies.get(companyName);
    if (!company.sites.has(h.site)) {
      company.sites.set(h.site, { key: `${company.key}|s:${h.site}`, label: h.site, icon: "🏢", children: [] });
    }
    company.sites.get(h.site).children.push(h);
  });

  return {
    key: "root",
    label: "All Countries",
    icon: "🌐",
    bold: true,
    children: [...countries.values()]
      .sort((a, b) => compareCountries(a.label, b.label, a.code, b.code))
      .map(({ companies, code, ...country }) => ({
      ...country,
      children: [...companies.values()].sort(byLabel).map(({ sites, ...company }) => ({
        ...company,
        children: [...sites.values()].sort(byLabel),
      })),
    })),
  };
};

const countLeaves = (node) => (node.serial ? 1 : node.children.reduce((n, c) => n + countLeaves(c), 0));

/** First H-LINK under `node` matching `match`, with the keys of the branches leading to it. */
const findLeaf = (node, match) => {
  if (node.serial) return match(node) ? { leaf: node, keys: [] } : null;
  for (const child of node.children) {
    const found = findLeaf(child, match);
    if (found) return { leaf: found.leaf, keys: [node.key, ...found.keys] };
  }
  return null;
};

/* Above 1024px the 1920px desktop composition is scaled to fit instead of reflowing. */
const DESIGN_WIDTH = 1920;
const MIN_SCALED_WIDTH = 1025;

function useDesktopScale() {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const update = () => {
      const width = window.innerWidth;
      const zoom = width >= MIN_SCALED_WIDTH ? width / DESIGN_WIDTH : 1;
      root.style.setProperty("--ehd-zoom", zoom.toFixed(4));
    };
    update();
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      root.style.removeProperty("--ehd-zoom");
    };
  }, []);
}

function TreeNode({ node, collapsed, onToggle, selectedSerial, onSelect, onSelectGroup }) {
  if (node.serial) {
    const isSelected = node.serial === selectedSerial;
    return (
      <li>
        <button
          type="button"
          className={`node${isSelected ? " selected" : ""}`}
          aria-pressed={isSelected}
          onClick={() => onSelect(node)}
          title={`H-LINK ${node.serial}`}
        >
          <span className={`dot-status ${node.status}`} />
          <span className="label">{node.label}</span>
          <span className="count">{node.online ? `${node.sensors} sens.` : "Offline"}</span>
        </button>
      </li>
    );
  }
  const isCollapsed = collapsed.has(node.key);
  const isRoot = node.key === "root";
  return (
    <li>
      <div className="node-row">
        <button
          type="button"
          className="node-caret"
          aria-expanded={!isCollapsed}
          aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${node.label}`}
          onClick={() => onToggle(node.key)}
        >
          {isCollapsed ? "›" : "⌄"}
        </button>
        <button
          type="button"
          className="node"
          onClick={() => (isRoot ? onToggle(node.key) : onSelectGroup(node))}
          title={isRoot ? undefined : `Show the first online H-LINK in ${node.label}`}
        >
          <span>{node.icon}</span>
          <span className="label">{node.bold ? <b>{node.label}</b> : node.label}</span>
          <span className="count">{countLeaves(node)}</span>
        </button>
      </div>
      {!isCollapsed && (
        <ul>
          {node.children.map((child) => (
            <TreeNode
              key={child.serial || child.key}
              node={child}
              collapsed={collapsed}
              onToggle={onToggle}
              selectedSerial={selectedSerial}
              onSelect={onSelect}
              onSelectGroup={onSelectGroup}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function SensorCard({ sensor, device, connectionType }) {
  const status = classify(sensor);
  const hot = isSensorOverheat(sensor, device?.groups?.IR_Temp_State?.flat() || [], connectionType);
  const tone = status === "emergency" ? "emergency" : status === "warning" || hot ? "warning" : "normal";
  const temp = Number(sensor.ir_temperature);
  const level = Math.max(0, Math.min(METER_BARS, Number(sensor.arc_level) || 0));
  const spot = Number(sensor.ir_spot_point) || 0;
  const flags = `${sensor.arc_state ? "A! " : ""}${hot ? "T!" : ""}`.trim();

  return (
    <div className={`sensor ${tone}`}>
      <div className="head">
        <span className="sn">ID {sensor.sensor_id}</span>
        <span className="sensor-status">{STATUS_LABEL[status]}</span>
      </div>
      <div className="temp">
        {flags ? <span className="flags">{flags}</span> : null}
        {temp > 0 ? `${temp}°C` : "---"}
      </div>
      <div className="foot">
        <div
          className="level"
          role="meter"
          aria-label="Arc level"
          aria-valuemin={0}
          aria-valuemax={METER_BARS}
          aria-valuenow={level}
          title={`Arc level ${level}`}
        >
          {Array.from({ length: METER_BARS }, (_, i) => (
            <span key={i} className={METER_BARS - i <= level ? "on" : undefined} />
          ))}
        </div>
        {spot > 0 ? (
          <div className="matrix" role="img" aria-label={`IR spot point ${spot}`}>
            {Array.from({ length: SPOT_CELLS }, (_, i) => (
              <span key={i} className={i + 1 === spot ? "on" : undefined} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function DashboardView({
  kpis,
  devices,
  activeDevice,
  installStore,
  onSelectHlink,
  connectionType,
  tempTrend,
  recentAlerts,
  recentTempAlerts,
  isOnline,
  notifications,
  showNotifications,
  setShowNotifications,
  dismissNotification,
  dismissAllNotifications,
  notificationRef,
  onTempGraph,
  hideThreshold,
  onReset,
  onThreshold,
  thresholdDisabled,
  t,
}) {
  useDesktopScale();
  const navigate = useNavigate();
  const { isDark, toggleMode } = useThemeMode();
  const chart = isDark ? CHART.dark : CHART.light;

  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [pickedSerial, setPickedSerial] = useState("");
  const [tripAlert, setTripAlert] = useState(null);
  const [tripResets, setTripResets] = useState({});
  const [tripAckToast, setTripAckToast] = useState(false);
  const [tripAckTick, setTripAckTick] = useState(0);
  const seenTrips = useRef(new Set());
  const ackedTripsUntil = useRef(new Map()); // tripId -> suppress until ms
  const tripAlertRef = useRef(null);
  const tripAckToastTimer = useRef(null);
  const tripAckResumeTimers = useRef([]);
  tripAlertRef.current = tripAlert;
  const TRIP_ACK_MS = 10 * 60 * 1000;

  useEffect(
    () => () => {
      if (tripAckToastTimer.current) clearTimeout(tripAckToastTimer.current);
      tripAckResumeTimers.current.forEach((id) => clearTimeout(id));
      tripAckResumeTimers.current = [];
    },
    []
  );

  const liveBySerial = useMemo(() => new Map((devices || []).map((d) => [serialOf(d), d])), [devices]);
  const locationKey = useMemo(() => locationKeyOf(devices), [devices]);
  const placements = useMemo(() => buildPlacements(locationKey, installStore), [locationKey, installStore]);
  const hlinks = useMemo(() => buildHlinks(placements, devices), [placements, devices]);
  const hlinkBySerial = useMemo(() => new Map(hlinks.map((h) => [h.serial, h])), [hlinks]);

  const q = query.trim().toLowerCase();
  const tree = useMemo(() => {
    const matches = q
      ? hlinks.filter((h) =>
          [h.label, h.serial, h.site, h.country, h.company].some((v) => String(v || "").toLowerCase().includes(q))
        )
      : hlinks;
    return buildTree(matches);
  }, [hlinks, q]);

  const siteSummary = useMemo(() => {
    const sites = new Map();
    hlinks.forEach((h) => {
      if (!sites.has(h.site)) sites.set(h.site, { site: h.site, hLinks: 0, on: 0, off: 0, sensors: 0 });
      const row = sites.get(h.site);
      row.hLinks += 1;
      row[h.online ? "on" : "off"] += 1;
      row.sensors += h.sensors;
    });
    return [...sites.values()];
  }, [hlinks]);

  // Offline H-LINKs can't become the live selection, so they are remembered locally.
  const activeSerial = serialOf(activeDevice);
  useEffect(() => setPickedSerial(""), [activeSerial]);
  const shownSerial = pickedSerial || activeSerial;
  const shownHlink = hlinkBySerial.get(shownSerial) || null;
  const shownDevice = liveBySerial.get(shownSerial) || null;
  const boardSensors = liveSensors(shownDevice);

  const selectHlink = (h) => {
    if (h.live) {
      setPickedSerial("");
      onSelectHlink?.(h.serial);
    } else {
      setPickedSerial(h.serial);
    }
  };

  /** Falls back to the first H-LINK when none in the branch is online. */
  const selectGroup = (node) => {
    const found = findLeaf(node, (h) => h.online) || findLeaf(node, () => true);
    if (!found) return;
    selectHlink(found.leaf);
    setCollapsed((prev) => {
      if (!found.keys.some((key) => prev.has(key))) return prev;
      const next = new Set(prev);
      found.keys.forEach((key) => next.delete(key));
      return next;
    });
  };

  const toggleBranch = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const locationOf = (serial, sensorId) => {
    if (!serial) return "—";
    const install = getSensorInstallDisplay(installStore, serial, sensorId);
    if (install.matched && install.location) return install.location;
    const h = hlinkBySerial.get(String(serial));
    return h ? `${h.site} › ${h.label}` : "—";
  };

  // ---------- Trip / MCB ----------
  const allTrips = (devices || []).flatMap((d) =>
    readTrips(d).map((trip) => ({ ...trip, hlinkSerial: serialOf(d), device: d }))
  );
  const tripReported = allTrips.length > 0;
  const trippedCount = allTrips.filter((t) => t.state === 1).length;
  const shownTrips = readTrips(shownDevice);

  // Each trip unit opens the alert once when Trip_State turns 1. Acknowledge
  // suppresses that unit for 10 minutes; otherwise it only reopens after reset to 0.
  useEffect(() => {
    const now = Date.now();
    ackedTripsUntil.current.forEach((until, id) => {
      if (until <= now) ackedTripsUntil.current.delete(id);
    });
    const tripped = allTrips.filter((t) => t.state === 1 && t.hlinkSerial && isDeviceOnline(t.device));
    const idOf = (t) => `${t.hlinkSerial}:${t.key}`;
    const active = new Set(tripped.map(idOf));
    seenTrips.current.forEach((id) => {
      if (!active.has(id)) seenTrips.current.delete(id);
    });
    if (tripAlertRef.current) return;
    const fresh = tripped.find((t) => {
      const id = idOf(t);
      const until = ackedTripsUntil.current.get(id);
      if (until && until > now) return false;
      return !seenTrips.current.has(id);
    });
    if (!fresh) return;
    seenTrips.current.add(idOf(fresh));
    setTripAlert({
      tripKey: fresh.key,
      hlinkSerial: fresh.hlinkSerial,
      tripSerial: fresh.serial,
      sensorId: fresh.sensorId,
      at: fresh.device?.device_timestamp || formatStamp(new Date()),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, tripAckTick]);

  const acknowledgeTripAlert = () => {
    if (!tripAlert) return;
    const id = `${tripAlert.hlinkSerial}:${tripAlert.tripKey ?? tripAlert.sensorId ?? ""}`;
    ackedTripsUntil.current.set(id, Date.now() + TRIP_ACK_MS);
    seenTrips.current.delete(id);
    setTripAlert(null);
    setTripAckToast(true);
    if (tripAckToastTimer.current) clearTimeout(tripAckToastTimer.current);
    tripAckToastTimer.current = setTimeout(() => setTripAckToast(false), 2200);
    const resumeId = window.setTimeout(() => setTripAckTick((n) => n + 1), TRIP_ACK_MS + 50);
    tripAckResumeTimers.current.push(resumeId);
  };

  // A reset request only matters while its unit is still tripped.
  useEffect(() => {
    const active = new Set(allTrips.filter((t) => t.state === 1).map((t) => `${t.hlinkSerial}:${t.key}`));
    setTripResets((prev) => {
      const kept = Object.entries(prev).filter(([id]) => active.has(id));
      return kept.length === Object.keys(prev).length ? prev : Object.fromEntries(kept);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices]);

  const resetTrip = async (hlinkSerial, trip) => {
    const id = `${hlinkSerial}:${trip.key}`;
    setTripResets((prev) => ({ ...prev, [id]: { status: "sending" } }));
    try {
      await sendTripReset(hlinkSerial, trip.sensorId ?? trip.key);
      setTripResets((prev) => ({ ...prev, [id]: { status: "sent" } }));
    } catch (error) {
      setTripResets((prev) => ({ ...prev, [id]: { status: "error", message: error?.message || "Request failed" } }));
    }
  };

  // ---------- Thermal ----------
  const trend = tempTrend || [];
  const last = trend[trend.length - 1];
  const irStats = {
    min: seriesStats(trend.map((s) => s.min)).min,
    avg: seriesStats(trend.map((s) => s.avg)).avg,
    max: seriesStats(trend.map((s) => s.max)).max,
  };
  const envStats = seriesStats(trend.map((s) => s.env));
  const humStats = seriesStats(trend.map((s) => s.hum));
  const metrics = [
    { icon: "🌡", label: "IR Temperature (°C)", color: "var(--cyan)", current: last?.avg, ...irStats },
    { icon: "◒", label: "Environment Temp (°C)", color: "var(--blue)", current: last?.env, ...envStats },
    { icon: "◉", label: "Humidity (%RH)", color: "var(--purple)", current: last?.hum, ...humStats },
  ];

  const insight = useMemo(() => {
    if (trippedCount > 0) return "An MCB trip is active. Check the tripped H-LINK before restoring power.";
    if (kpis.emergency > 0) return "Arc emergency is active. Review the arc alert list.";
    if (kpis.warning > 0) return "Warning sensors need a threshold check.";
    if (last?.avg != null) return "IR temperature and ambient temperature are stable overall.";
    return "Waiting for live sensor data.";
  }, [trippedCount, kpis.emergency, kpis.warning, last]);

  const pct = (v) => `${(Number(v) || 0).toFixed(1)}%`;
  const kpiItems = [
    { icon: "◉", label: "Total Sensors", value: kpis.total, badge: "Live fleet", tone: "cyan" },
    { icon: "✓", label: "Normal", value: kpis.normal, badge: pct(kpis.pctNormal), tone: "green", valueColor: "var(--green)" },
    { icon: "⚠", label: "Warning", value: kpis.warning, badge: pct(kpis.pctWarning), tone: "amber" },
    { icon: "!", label: "Emergency", value: kpis.emergency, badge: pct(kpis.pctEmergency), tone: "red" },
    { icon: "⌁", label: "No Response", value: kpis.offline, badge: pct(kpis.pctOffline), tone: "slate" },
  ];

  const crumbs = shownHlink ? [shownHlink.country, shownHlink.company || UNASSIGNED, shownHlink.site] : [];
  const alertHlink = tripAlert ? hlinkBySerial.get(tripAlert.hlinkSerial) : null;
  const alertSensorLocation =
    tripAlert && tripAlert.sensorId != null
      ? getSensorInstallDisplay(installStore, tripAlert.hlinkSerial, tripAlert.sensorId)
      : null;
  const alertLocation = alertHlink
    ? [alertHlink.country, alertHlink.site, alertHlink.label, alertSensorLocation?.matched ? alertSensorLocation.location : ""]
        .filter(Boolean)
        .join(" › ")
    : "—";

  return (
    <div className={`ehd${isDark ? "" : " light"}`}>
      <main className="main">
        <div className="topbar">
          <div className="title">
            <h1 className="brand-title">
              <span className="brand-e">e</span>
              <span className="brand-hyphen">-</span>
              <span className="brand-name">HAECHIE</span>
              <span className="brand-rest">Sensor Real-time Monitoring</span>
            </h1>
            <div className="subtitle">
              Electrical Safety for a Safer Tomorrow
            </div>
          </div>
          <div className="tools">
            <button
              type="button"
              className="toolbtn"
              onClick={toggleMode}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? "☀" : "☾"}
            </button>
            <span className="pill">📍 {shownHlink ? `${shownHlink.site}, ${shownHlink.country}` : "—"}</span>
            <LiveClock />
            <div className="bell" ref={notificationRef}>
              <button
                type="button"
                className="toolbtn"
                aria-label="Notifications"
                aria-expanded={showNotifications}
                onClick={() => setShowNotifications((v) => !v)}
              >
                🔔{notifications.length > 0 ? <span className="count">{Math.min(99, notifications.length)}</span> : null}
              </button>
              {showNotifications ? (
                <div className="notif">
                  <div className="notif-h">
                    <b>Notifications</b>
                    <div>
                      {notifications.length > 0 ? (
                        <button type="button" className="linkbtn" onClick={dismissAllNotifications}>
                          Clear
                        </button>
                      ) : null}
                      <button type="button" className="x" aria-label="Close" onClick={() => setShowNotifications(false)}>
                        ✕
                      </button>
                    </div>
                  </div>
                  {notifications.length === 0 ? (
                    <div className="none">No new alerts</div>
                  ) : (
                    <ul>
                      {notifications.map((a) => (
                        <li key={a.id}>
                          <span>{a.message}</span>
                          <button type="button" className="x" aria-label="Dismiss" onClick={() => dismissNotification(a.id)}>
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <section className="kpis">
          {kpiItems.map((kpi) => (
            <div key={kpi.label} className="kpi">
              <div className={`ic ${kpi.tone}`}>{kpi.icon}</div>
              <div>
                <small>{kpi.label}</small>
                <div className="kpi-value">
                  <strong style={kpi.valueColor ? { color: kpi.valueColor } : undefined}>{kpi.value}</strong>
                  <span className={`badge ${kpi.tone}`}>{kpi.badge}</span>
                </div>
              </div>
            </div>
          ))}
          <div className={`kpi trip${trippedCount > 0 ? " active" : ""}`}>
            <div className={`ic ${trippedCount > 0 ? "red" : tripReported ? "green" : "slate"}`}>⚡</div>
            <div>
              <small>Trip / MCB State</small>
              <div className="kpi-value">
                <strong>{tripReported ? trippedCount : "—"}</strong>
                <span className={`badge ${trippedCount > 0 ? "red" : tripReported ? "green" : "slate"}`}>
                  {trippedCount > 0 ? "MCB Trip" : tripReported ? "No MCB Trip" : "No data"}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="dashboard">
          <div className="leftcol">
            <div className="card">
              <div className="card-h">
                <h3>Site Hierarchy</h3>
                <span className="badge blue">
                  {siteSummary.length} site{siteSummary.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="tree">
                <div className="search">
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search site, location or H-LINK..."
                  />
                </div>
                {tree.children.length === 0 ? (
                  <div className="none">{hlinks.length ? "No matching H-LINKs" : "Waiting for devices…"}</div>
                ) : (
                  <ul>
                    <TreeNode
                      node={tree}
                      collapsed={collapsed}
                      onToggle={toggleBranch}
                      selectedSerial={shownSerial}
                      onSelect={selectHlink}
                      onSelectGroup={selectGroup}
                    />
                  </ul>
                )}
              </div>
              <div className="site-summary">
                <div className="row head">
                  <b>Site</b>
                  <small>H-L</small>
                  <small>On</small>
                  <small>Off</small>
                  <small>Sens.</small>
                </div>
                {siteSummary.map((row) => (
                  <div key={row.site} className="row">
                    <span title={row.site}>{row.site}</span>
                    <b>{row.hLinks}</b>
                    <b style={{ color: "var(--green)" }}>{row.on}</b>
                    <b style={row.off > 0 ? { color: "var(--red)" } : undefined}>{row.off}</b>
                    <b>{row.sensors}</b>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="center">
            <div className="card sensor-board">
              <div className="crumbs">
                {crumbs.map((part, i) => (
                  <React.Fragment key={i}>
                    {part} &nbsp;›&nbsp;{" "}
                  </React.Fragment>
                ))}
                <b>{shownHlink ? shownHlink.label : "No H-LINK selected"}</b>
                {shownHlink ? (
                  <>
                    {" "}
                    &nbsp;·&nbsp; HLINK-{shownHlink.serial} &nbsp;·&nbsp; {boardSensors.length} sensor
                    {boardSensors.length === 1 ? "" : "s"}
                  </>
                ) : null}
              </div>
              <div className="sensor-grid">
                {boardSensors.map((sensor) => (
                  <SensorCard key={sensor.sensor_id} sensor={sensor} device={shownDevice} connectionType={connectionType} />
                ))}
                {boardSensors.length === 0 ? (
                  <div className="empty">
                    {!shownHlink
                      ? "Waiting for devices…"
                      : shownDevice
                      ? "No sensors reporting on this H-LINK"
                      : "This H-LINK is not connected"}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="card thermal">
              <div className="thermal-head">
                <div>
                  <h3>Thermal &amp; Environment Overview</h3>
                  <div className="subtitle">IR temperature + ambient environment sensor temperature</div>
                </div>
                <span className={`badge ${isOnline ? "green" : "slate"}`}>
                  {isOnline ? `● Live · last ${trend.length} samples` : "Offline"}
                </span>
              </div>
              <div className="thermal-body">
                <div className="metrics">
                  {metrics.map((m) => (
                    <div key={m.label} className="metric">
                      <span style={{ color: m.color }}>{m.icon}</span>
                      <div>
                        <small>{m.label}</small>
                        <b style={{ color: m.color }}>{fmt1(m.current)}</b>
                      </div>
                      <span>{fmt1(m.min)}</span>
                      <span>{fmt1(m.avg)}</span>
                      <span>{fmt1(m.max)}</span>
                    </div>
                  ))}
                  <div className="metric-legend">
                    <span>Min</span>
                    <span>Avg</span>
                    <span>Max</span>
                  </div>
                </div>
                <div className="chart">
                  <div className="legend">
                    <span>
                      <i className="dot" style={{ background: chart.ir }} />
                      IR Temperature
                    </span>
                    <span>
                      <i className="dot" style={{ background: chart.env }} />
                      Environment Temperature
                    </span>
                    <span>
                      <i className="dot" style={{ background: chart.hum }} />
                      Humidity
                    </span>
                  </div>
                  <div className="chart-area">
                    {trend.length < 2 ? (
                      <div className="wait">Collecting live samples…</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trend} margin={{ top: 10, right: 8, left: -14, bottom: 0 }}>
                          <CartesianGrid stroke={chart.grid} vertical={false} />
                          <XAxis dataKey="time" tick={{ fill: chart.muted, fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={36} />
                          <YAxis tick={{ fill: chart.muted, fontSize: 10 }} axisLine={false} tickLine={false} width={40} />
                          <Tooltip
                            contentStyle={{
                              background: chart.tooltip,
                              border: `1px solid ${chart.line}`,
                              borderRadius: 10,
                              fontSize: 12,
                              color: chart.text,
                            }}
                          />
                          <Line type="monotone" dataKey="avg" name="IR °C" stroke={chart.ir} strokeWidth={3} dot={false} connectNulls isAnimationActive={false} />
                          <Line type="monotone" dataKey="env" name="Env °C" stroke={chart.env} strokeWidth={3} dot={false} connectNulls isAnimationActive={false} />
                          <Line type="monotone" dataKey="hum" name="Humidity %RH" stroke={chart.hum} strokeWidth={3} dot={false} connectNulls isAnimationActive={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rightcol">
            <div className={`card trip-card${shownTrips.length ? "" : " faded"}`} aria-disabled={!shownTrips.length}>
              <div className="card-h">
                <h3>Trip / MCB State</h3>
                {shownTrips.length ? (
                  <span className={`badge ${isDeviceOnline(shownDevice) ? "green" : "slate"}`}>
                    {isDeviceOnline(shownDevice) ? "● Live" : "Offline"}
                  </span>
                ) : null}
              </div>
              <div className="trip-panel">
                <div className="trip-device">
                  <h4>{shownHlink ? `HLINK-${shownHlink.serial}` : "No H-LINK selected"}</h4>
                  <div className="kv">
                    <span>Protocol</span>
                    <b>{shownDevice ? protocolOf(shownDevice, connectionType) : "—"}</b>
                    <span>Trip Units</span>
                    <b>{shownTrips.length}</b>
                  </div>
                </div>
                <div className="trip-list">
                  {shownTrips.length === 0 ? (
                    <div className="trip-state unknown">
                      <div className="trip-icon">⚡</div>
                      <div className="trip-copy">
                        <div className="trip-label">
                          Serial No. <b>—</b>
                        </div>
                        <div className="trip-value">
                          <strong>—</strong>
                          <span>No trip data</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    shownTrips.map((trip) => {
                      const reset = tripResets[`${shownSerial}:${trip.key}`];
                      return (
                        <div key={trip.key} className={`trip-state${trip.state === 1 ? " trip" : ""}`}>
                          <div className="trip-icon">{trip.state === 1 ? "⚡" : "✓"}</div>
                          <div className="trip-copy">
                            <div className="trip-label">
                              Serial No. <b>{trip.serial || "—"}</b>
                            </div>
                            <div className="trip-value">
                              <strong>{trip.state}</strong>
                              <span>{trip.state === 1 ? "MCB Down · Protective Trip" : "No MCB Trip · MCB Up"}</span>
                            </div>
                            {reset?.status === "error" ? <div className="trip-reset-error">{reset.message}</div> : null}
                          </div>
                          {trip.state === 1 ? (
                            <button
                              type="button"
                              className="trip-reset"
                              disabled={reset?.status === "sending" || reset?.status === "sent"}
                              onClick={() => resetTrip(shownSerial, trip)}
                              title="Send Trip_State 0 to this trip unit"
                            >
                              {reset?.status === "sending" ? "Sending…" : reset?.status === "sent" ? "Sent" : "Set 0"}
                            </button>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-h">
                <h3>Global Deployment</h3>
                <button type="button" className="badge blue" onClick={() => navigate("/installation-map")}>
                  Installation Info
                </button>
              </div>
              <DashboardSiteMap hlinks={hlinks} selectedSerial={shownSerial} onSelect={selectHlink} isDark={isDark} />
            </div>
          </div>
        </section>

        <section className="tables">
          <div className="card table-card">
            <div className="card-h">
              <h3>Arc Alerts</h3>
              <button type="button" className="badge blue" onClick={() => navigate("/logs")}>
                View All
              </button>
            </div>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>H-LINK</th>
                    <th>Sensor</th>
                    <th>Location</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(recentAlerts || []).length === 0 ? (
                    <tr>
                      <td className="none" colSpan={5}>
                        No recent arc alerts
                      </td>
                    </tr>
                  ) : (
                    recentAlerts.map((a) => (
                      <tr key={a.id}>
                        <td>{a.time}</td>
                        <td>{a.serial ? `HLINK-${a.serial}` : "—"}</td>
                        <td>ID {a.sensorId}</td>
                        <td className="loc">{locationOf(a.serial, a.sensorId)}</td>
                        <td>
                          <span className={`badge ${a.tone === "red" ? "red" : a.tone === "amber" ? "amber" : "slate"}`}>{a.level}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card table-card">
            <div className="card-h">
              <h3>Temperature Alerts</h3>
              <button type="button" className="badge blue" onClick={() => navigate("/logs")}>
                View All
              </button>
            </div>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Sensor</th>
                    <th>Location</th>
                    <th>Current</th>
                    {!hideThreshold ? <th>Threshold</th> : null}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(recentTempAlerts || []).length === 0 ? (
                    <tr>
                      <td className="none" colSpan={hideThreshold ? 5 : 6}>
                        No temperature alerts
                      </td>
                    </tr>
                  ) : (
                    recentTempAlerts.map((a) => (
                      <tr key={a.id}>
                        <td>{a.time}</td>
                        <td>ID {a.sensorId}</td>
                        <td className="loc">{locationOf(a.serial, a.sensorId)}</td>
                        <td className="hot">{a.temperature != null ? `${a.temperature}°C` : "—"}</td>
                        {!hideThreshold ? <td>{a.threshold != null ? `${a.threshold}°C` : "—"}</td> : null}
                        <td>
                          <button
                            type="button"
                            className="linkbtn"
                            onClick={() =>
                              onTempGraph?.({
                                id: a.logId,
                                sensor_id: a.sensorId,
                                date: a.date,
                                time: a.fullTime,
                                threshold: hideThreshold ? null : a.threshold,
                                hideThreshold,
                                temperature: a.temperature,
                              })
                            }
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <div className="card insight">
          <div>
            <b>AI Insights</b> &nbsp; {insight}
          </div>
          <div className="actions">
            <button type="button" className="primary" onClick={onReset}>
              {t?.("dashboard.resetWarnings") || "Reset Warnings"}
            </button>
            {!hideThreshold ? (
              <button type="button" onClick={onThreshold} disabled={thresholdDisabled}>
                {t?.("dashboard.thresholdConfig") || "Threshold Config"}
              </button>
            ) : null}
            <button type="button" className="dark" onClick={() => navigate("/ai-model-beta")}>
              View AI Insights
            </button>
          </div>
        </div>
      </main>

      <div className="footer">
        <AppFooter className={isDark ? "!text-[#93a4c3]" : "!text-slate-500"} />
      </div>

      {tripAlert ? (
        <div
          className="overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setTripAlert(null);
          }}
        >
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="ehd-trip-modal-title">
            <div className="modal-h">
              <h2 id="ehd-trip-modal-title">⚠ MCB Trip Alert</h2>
              <button type="button" className="toolbtn" onClick={() => setTripAlert(null)} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="modal-b">
              <div className="alertbox">
                <div className="alerticon">⚡</div>
                <div>
                  <h3>Arc event triggered protective trip. MCB box is down.</h3>
                  <div className="kv">
                    <span>H-LINK Serial No.</span>
                    <b>{tripAlert.hlinkSerial}</b>
                    <span>Trip Serial No.</span>
                    <b>{tripAlert.tripSerial || "—"}</b>
                    <span>Protocol</span>
                    <b>{protocolOf(liveBySerial.get(tripAlert.hlinkSerial), connectionType)}</b>
                    <span>Sensor ID</span>
                    <b>{tripAlert.sensorId ?? "—"}</b>
                  </div>
                </div>
                <div className="kv">
                  <span>Trip State</span>
                  <b className="danger-value">1</b>
                  <span>MCB Status</span>
                  <b className="danger-value">DOWN</b>
                  <span>Event Time</span>
                  <b>{tripAlert.at}</b>
                  <span>Location</span>
                  <b>{alertLocation}</b>
                </div>
              </div>
            </div>
            <div className="modal-f">
              <button type="button" className="ack" onClick={acknowledgeTripAlert}>
                Acknowledge
              </button>
              <button
                type="button"
                onClick={() => {
                  const h = hlinkBySerial.get(tripAlert.hlinkSerial);
                  if (h) selectHlink(h);
                  setTripAlert(null);
                }}
              >
                View Device
              </button>
              <button type="button" onClick={() => setTripAlert(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className={`toast${tripAckToast ? " show" : ""}`} role="status">
        Trip alert acknowledged · hidden for 10 minutes
      </div>
    </div>
  );
}
